import Dialog from "@components/dialog";
import { fetchProductPrices } from "@components/purchaseWizardDialog/PurchaseWizardDialog.utils";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { fetchItemsByInfo } from "@components/smart/smartSelect/SmartSelectAPI";
import BindingContext, { createPath } from "@odata/BindingContext";
import { ODataError } from "@odata/Data.types";
import { getNestedValue, getNewItemsMaxId } from "@odata/Data.utils";
import {
    CompanyEntity,
    CompanyLegalAddressEntity,
    CompanyVatStatusTemporalCurrentEntity,
    CompanyVatStatusTemporalEntity,
    EntitySetName,
    ICompanyCommunicationAddressEntity,
    ICompanyModuleEntity,
    TaxStatementSignatoryPersonEntity
} from "@odata/GeneratedEntityTypes";
import {
    AccountingCode,
    GeneralPermissionCode,
    ModuleCode,
    SignatoryPersonCode,
    TaxStatementSubmitterCode,
    TypeOfBusinessCode,
    VatStatementFrequencyCode,
    VatStatusCode
} from "@odata/GeneratedEnums";
import { BatchRequest, IBatchResult } from "@odata/OData";
import { handleCurrentTemporalPropChange } from "@odata/TemporalUtils";
import { getNextSystemCompanyLogoId } from "@utils/CompanyUtils";
import { isNotDefined, isObjectEmpty } from "@utils/general";
import { logger } from "@utils/log";
import { getOneFetch, isAbortException } from "@utils/oneFetch";
import { removeWhiteSpace } from "@utils/string";
import { cloneDeep } from "lodash";
import React from "react";

import { FU_BY_POSTAL_CODE_URL } from "../../constants";
import { AppContext } from "../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../contexts/permissionContext/withPermissionContext";
import { Status } from "../../enums";
import { AwaitedArray, toTuple } from "../../global.types";
import {
    FormStorage,
    IContextInitArgs,
    IFormStorageSaveResult,
    ISaveArgs,
    IValidateAndSave,
    TCustomResponseHandler,
    TShouldUseCustomResHandler
} from "../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../views/formView/FormView";
import ConfirmationButtons from "../../views/table/ConfirmationButtons";
import { companyIsSecondaryAddressPath } from "./Company.shared.utils";
import { ICompanyExtendedEntity, ICompanyFormCustomData } from "./Company.types";
import {
    getVatDeductionCoefficientCurrentYear,
    handleCompanyCreated,
    hasGrantPath,
    hasPayrollEvidencePath,
    ossBankAccountPath,
    partnerAresFields,
    VatReducedDeductionCoefficient
} from "./Company.utils";
import CompanyAvatarSelector from "./CompanyAvatarSelector";
import { hasTaxStatementSignatoryPersonPath } from "./TaxStatementSignatoryPerson.utils";

interface ICompanyFormViewProps extends IFormViewProps<ICompanyExtendedEntity, ICompanyFormCustomData> {
    scrollRef?: React.RefObject<HTMLDivElement>;
}

const oneFetch = getOneFetch();

const POSTAL_CODE_LENGTH = 5;

const NON_PROFIT_FORMS = [701, 706, 736, 906, 936];

const VatStatusErrors = [
    "CompanyVatStatusCannotModifyActiveStatusWhenElectronicSubmissionAlreadyExists",
    "CompanyVatStatusCannotModifyActiveStatusWhenElectronicSubmissionAlreadyExists",
    "CompanyVatStatusSwitchFromMonthFrequencyToQuarterDateIsInvalid",
    "CompanyVatStatusSwitchFromQuarterFrequencyToMonthDateIsInvalid",
    "CompanyVatStatusSwitchFromMonthFrequencyToMonthIsInvalid",
    "CompanyVatStatusSwitchFromQuarterFrequencyToQuarterIsInvalid",
    "CompanyVatStatusMoreThanOneChangeInFutureIsInvalid",
    "CompanyVatStatusSwitchFromVatRegisteredToNotVatRegisteredWhileElectronicSubmissionExists",
    "CompanyVatStatusSwitchFromNotVatRegisteredToNotVatRegisteredIsInvalid",
    "CompanyVatStatusSwitchFromNotVatRegisteredToQuarterIsInvalid",
    "CompanyVatStatusElectronicSubmissionAlreadyExistsSwitchIsInvalid",
    "CompanyVatStatusFirstElectronicSubmissionAlreadyExistsInFutureMonths",
    "CompanyVatStatusElectronicSubmissionExistsForDifferentFrequency",
    "CompanyVatStatusSwitchFromVatRegisteredToIdentifiedPersonIsInvalid"
];

class CompanyFormView extends FormViewForExtend<ICompanyExtendedEntity, ICompanyFormViewProps> {
    static contextType = AppContext;

    _communicationAddressBackup: ICompanyCommunicationAddressEntity;
    _isMounted: boolean;

    constructor(props: ICompanyFormViewProps) {
        super(props);
        this.props.storage.setCustomData({ hasPermission: props.permissionContext.generalPermissions.has(GeneralPermissionCode.CompanyManagement) });
    }

    async componentDidMount() {
        super.componentDidMount();
        this._isMounted = true;

        this.props.storage.setFormAlert(null);
        if (this.isReady()) {
            // trigger onAfterLoad manually as the storage is already loaded before we subscribed to onAfterLoad event
            // ! beware, this can be called multiple times, if use switch between avatar selection and company form !
            //      keep that in mind in onAfterLoad handlers
            this.onAfterLoad();
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        this._isMounted = false;
    }

    get postalCodePath(): string {
        return createPath(CompanyEntity.LegalAddress, CompanyLegalAddressEntity.PostalCode);
    }

    setBusy(busy: boolean): void {
        this.context?.setBusy ? this.context.setBusy(busy) : this.props.storage.setBusy(busy);
    }

    getAdditionalLoadPromise(args: IContextInitArgs) {
        const productCode = this.props.storage.context.getData().tenant.ProductCode;

        return toTuple([
            fetchProductPrices(this.props.storage.oData, productCode),
            // getLastVatSubmissionLockDate(this.props.storage.oData),
            ...super.getAdditionalLoadPromise(args)
        ]);
    }

    getAdditionalResults(): AwaitedArray<ReturnType<CompanyFormView["getAdditionalLoadPromise"]>> {
        return this.props.storage.data.additionalResults;
    }

    setModuleFlag = (moduleCode: ModuleCode, path: string) => {
        const module = this.entity.Modules?.find(module => module.ModuleCode === moduleCode);
        this.props.storage.setValueByPath(path, !!module?.IsEnabled);
    }

    onAfterLoad(): Promise<void> {
        this.updateOssBankAccount();
        this.addLogoToNewCompany();
        this.handleInitialIsSecondaryAddress(this.props);
        this.setCurrentDeductionCoefficient();

        this.props.storage.setValueByPath(hasTaxStatementSignatoryPersonPath, !this.props.storage.data.bindingContext.isNew() && !isObjectEmpty(this.entity.TaxStatementSignatoryPerson));
        this.props.storage.setCustomData({ productPrices: this.getAdditionalResults()[0].value });
        // this.props.storage.setCustomData({ vatDecisiveDate: this.getAdditionalResults()[1] });

        this.setModuleFlag(ModuleCode.Payroll, hasPayrollEvidencePath);
        this.setModuleFlag(ModuleCode.Grants, hasGrantPath);

        this.setBusy(false);
        return super.onAfterLoad();
    }

    updateOssBankAccount = (): void => {
        const accounts = this.props.storage.context.getCompanyBankAccounts() ?? [];
        const ossAccount = accounts.find(account => account.IsOss);

        this.props.storage.setValueByPath(ossBankAccountPath, ossAccount);
        this.props.storage.refreshFields();
    };

    setCurrentDeductionCoefficient = (): void => {
        const currentYear = getVatDeductionCoefficientCurrentYear();
        const sortedCoefficients = this.entity.VatReducedCoefficients?.sort((a, b) => b.Year - a.Year) ?? [];
        const currentCoeff = sortedCoefficients.find(coeff =>
            coeff.Year === currentYear
        ) ?? sortedCoefficients?.[0];
        this.props.storage.setValueByPath(VatReducedDeductionCoefficient, currentCoeff?.VatReducedCoefficient);
    };

    addLogoToNewCompany = (): void => {
        const { storage } = this.props;
        const company = this.entity;

        if (company.Logo) {
            return;
        }

        const nextCompanyLogoId = getNextSystemCompanyLogoId(storage.context.getData().companies);

        company.Logo = { Id: nextCompanyLogoId };
    };

    handleInitialIsSecondaryAddress = (props: ICompanyFormViewProps): void => {
        // set Companies/IsSecondaryAddress switch based on CommunicationAddress value
        const isSecondaryAddress = !(props.storage.data.bindingContext.isNew() || !this.entity.CommunicationAddress?.City);

        this._communicationAddressBackup = this.entity.CommunicationAddress;
        props.storage.clearAndSetValue(props.storage.data.bindingContext.navigate(companyIsSecondaryAddressPath), isSecondaryAddress);
        if (!isSecondaryAddress) {
            this.entity.CommunicationAddress = {};
        }
    };

    get scrollRef(): React.RefObject<HTMLElement> {
        // inside dialog, we need to use its scroller, not the form one
        return this.props.scrollRef ?? this._refScroll;
    }

    scrollPageDown = (): void => {
        if (this.scrollRef?.current) {
            this.scrollRef.current.scrollTop = this.scrollRef.current.scrollHeight;
        }
    };

    scrollPageUp = (): void => {
        if (this.scrollRef?.current) {
            this.scrollRef.current.scrollTop = 0;
        }
    };

    shouldUseCustomResponseHandler: TShouldUseCustomResHandler = (response: IBatchResult[]) => {
        const error = response[0] as IBatchResult<ODataError>;
        const vatStatusError = error.body?._validationMessages?.find(message => VatStatusErrors.includes(message.code) || message.code.startsWith("CompanyVatStatus"));

        // vat statuses are set in dialog, but saved with the whole form,
        // we need to use custom error handling to show the correct error in the form
        return !!vatStatusError;
    };

    customResponseHandler: TCustomResponseHandler = async (response: IBatchResult[], args: IValidateAndSave, formStorage: FormStorage) => {
        const error = response[0] as IBatchResult<ODataError>;
        const vatStatusError = error.body?._validationMessages?.find(message => VatStatusErrors.includes(message.code) || message.code.startsWith("CompanyVatStatus"));

        if (vatStatusError) {
            this.props.storage.setFormAlert({
                status: Status.Error,
                title: this.props.storage.t("Common:General.FormValidationErrorTitle"),
                subTitle: this.props.storage.t(`Error:${vatStatusError.code}`)
            });
        }

        return null;
    };

    handleOssBankAccountSave = async (batch: BatchRequest): Promise<void> => {
        const ossBankAccountIdPath = createPath(ossBankAccountPath, "Id");
        const ossBankAccountId = this.props.storage.getValueByPath(ossBankAccountIdPath);
        const accounts = this.props.storage.context.getCompanyBankAccounts() ?? [];
        const originalOssAccountId = accounts.find(account => account.IsOss)?.Id;

        if (ossBankAccountId === originalOssAccountId) {
            return;
        }

        if (originalOssAccountId) {
            batch.getEntitySetWrapper(EntitySetName.CompanyBankAccounts).update(originalOssAccountId, { IsOss: false });
        }

        if (ossBankAccountId) {
            batch.getEntitySetWrapper(EntitySetName.CompanyBankAccounts).update(ossBankAccountId, { IsOss: true });
        }
    };

    onBeforeSave(): ICompanyExtendedEntity {
        const storage = this.props.storage;
        const entity: ICompanyExtendedEntity = cloneDeep(this.entity);
        if (storage.getValueByPath(hasTaxStatementSignatoryPersonPath)) {
            entity.TaxStatementSignatoryPerson = storage.clearHiddenFields(this.entity.TaxStatementSignatoryPerson, storage.data.bindingContext.navigate(CompanyEntity.TaxStatementSignatoryPerson));
        } else {
            entity.TaxStatementSignatoryPerson = null;
        }

        if (!entity.Name) {
            entity.Name = entity.LegalName;
        }

        const currentYear = getVatDeductionCoefficientCurrentYear();
        if (entity.UseVatReducedDeduction) {
            const lastCoeffValue = storage.getValueByPath(VatReducedDeductionCoefficient);
            const lastCoeffEntity = entity.VatReducedCoefficients?.find(item => item.Year === currentYear);
            if (lastCoeffEntity) {
                lastCoeffEntity.VatReducedCoefficient = lastCoeffValue;
            } else {
                if (!entity.VatReducedCoefficients) {
                    entity.VatReducedCoefficients = [];
                }
                entity.VatReducedCoefficients.push({
                    Year: currentYear,
                    VatReducedCoefficient: lastCoeffValue
                });
            }
        } else {
            entity.VatReducedCoefficients = entity.VatReducedCoefficients?.filter(item => item.Year !== currentYear);
        }

        if (!entity[companyIsSecondaryAddressPath]) {
            // remove partially filled communication address if it is hidden in form
            entity.CommunicationAddress = null;
        }

        if (entity.TypeOfBusinessCode !== TypeOfBusinessCode.NaturalPerson) {
            // LegalPerson doesn't have BirthNumber
            entity.BirthNumber = null;
        }

        // update TemporalPropertyBag
        for (let i = 0; i < entity.TemporalPropertyBag.length; i++) {
            const bag = entity.TemporalPropertyBag[i];

            if ([VatStatusCode.NotVATRegistered, VatStatusCode.IdentifiedPerson].includes(bag.VatStatusCode)) {
                // in case we change it back from VatPayer, we need to set null to delete the code from DB
                bag.VatStatementFrequencyCode = null;
                bag.VatStatementFrequency = null;
                bag.IsOssMode = false;
            }
        }

        const _addOrUpdateModule = (moduleCode: ModuleCode, path: string) => {
            const module = entity.Modules?.find(module => module.ModuleCode === moduleCode);
            if (module) {
                module.IsEnabled = storage.getValueByPath(path);
            } else {
                const newModuleId = getNewItemsMaxId(entity.Modules);
                const newModule = BindingContext.createNewEntity<ICompanyModuleEntity>(newModuleId, {
                    ModuleCode: moduleCode,
                    IsEnabled: storage.getValueByPath(path)
                });
                entity.Modules = [...(entity.Modules ?? []), newModule];
            }
        };

        _addOrUpdateModule(ModuleCode.Payroll, hasPayrollEvidencePath);

        if (this.props.storage.getValueByPath(CompanyEntity.AccountingCode) !== AccountingCode.CashBasisAccounting) {
            _addOrUpdateModule(ModuleCode.Grants, hasGrantPath);
        }

        return entity;
    }

    save = async (saveArgs?: Partial<ISaveArgs>): Promise<IFormStorageSaveResult> => {
        this.setBusy(true);
        const { storage } = this.props;

        this.props.onBeforeSave?.();
        const saveData = this.onBeforeSave();

        const isNew = storage.data.bindingContext.isNew();
        const successSubtitle = storage.t("Companies:Validation.Saved");
        const result = await this.props.storage.save({
            data: saveData,
            successSubtitle,
            // new company is created in a dialog, which should be without error alert
            fieldValidationWithoutErrorAlert: isNew,
            shouldUseCustomResponseHandler: this.shouldUseCustomResponseHandler,
            customResponseHandler: this.customResponseHandler,
            ...saveArgs,
            onBeforeExecute: this.handleOssBankAccountSave,
            // handle manually, because of the oss bank accounts
            skipLoad: true
        });

        if (!result) {
            this.setBusy(false);
            this.props.onSaveFail?.();
            this.forceUpdate(this.scrollPageUp);
            return result;
        }


        // refresh companies list in context
        const { context } = this.props.storage;

        // refresh possibly updated bank accounts (and oss account)
        // and only then trigger onAfterLoad
        await context.updateCompanyBankAccounts();
        await this.props.storage.loadDataAfterSave();
        this.props.storage.displaySaveOkMessage(successSubtitle);

        if (isNew) {
            const { Id } = result.data;
            await handleCompanyCreated(context, Id);
        } else {
            await context.updateCompanies();
            context.rerenderApp();
        }

        this.onAfterSave(isNew, false);

        return result;
    };

    handlePostalCodeChange = async (e: ISmartFieldChange): Promise<void> => {
        try {
            const path = e.bindingContext.getEntityPath(true);
            if ((([CompanyEntity.LegalName, CompanyEntity.LegalNumber] as string[]).includes(path) && e.triggerAdditionalTasks) || path === this.postalCodePath) {
                const storage = this.props.storage;
                const postalCode = path === this.postalCodePath ? removeWhiteSpace(e.value as string) : e.additionalData?.BillingAddress?.PostalCode;
                let FA = null;
                // valid czech postal code has always 5 digits, so we don't have to fetch data otherwise
                if (postalCode?.length === POSTAL_CODE_LENGTH) {
                    const response = await oneFetch.fetch(`${FU_BY_POSTAL_CODE_URL}${postalCode}`);
                    if (!this._isMounted) {
                        return;
                    }
                    if (!response.ok) {
                        // ux wants to clear when you write down bad postal code, or postal code without assigned financial administration
                        logger.error("couldn't fetch financial administration data from postal code");
                    } else {
                        FA = await response.json();
                    }
                }
                storage.setValueByPath(CompanyEntity.FinancialAdministration, FA);
                storage.refreshGroupByKey("Settings");
            }
        } catch (e) {
            if (isAbortException(e)) {
                return;
            } else {
                throw e;
            }
        }
    };

    handleTaxStatementSignatoryPersonChange = (e: ISmartFieldChange): void => {
        const storage = this.props.storage;
        const signatoryPath = createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.SignatoryPerson);
        const path = e.bindingContext.getPath();

        if (path === TaxStatementSignatoryPersonEntity.TaxStatementSubmitter) {
            if (e.value === TaxStatementSubmitterCode.TaxAdvisor) {
                const personTypeCode = storage.getValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.PersonTypeCode));
                storage.setValueByPath(signatoryPath, personTypeCode === TypeOfBusinessCode.LegalPerson ? SignatoryPersonCode._4cLegalEntityProvidingTaxConsultancy : SignatoryPersonCode._4bIndividual_TaxAdvisorOrAttorney);
            }
            if (e.value === TaxStatementSubmitterCode.Accountant) {
                storage.setValueByPath(signatoryPath, SignatoryPersonCode._4aGeneralAttorneyIndividualAndLegalPerson);
            }
            if (e.value === TaxStatementSubmitterCode.Customer) {
                storage.setValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.PersonType), null);
                storage.setValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.SignatoryPersonCode), null);
            } else if (isNotDefined(storage.getValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.PersonType)))) {
                storage.setValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.PersonType), TypeOfBusinessCode.NaturalPerson);
            }
        }

        if (e.bindingContext.getParent().getPath(true) === CompanyEntity.TaxStatementSignatoryPerson &&
            (path === TaxStatementSignatoryPersonEntity.LegalPersonName || e.bindingContext.getPath() === TaxStatementSignatoryPersonEntity.LegalNumber) && e.triggerAdditionalTasks) {
            storage.setValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.LegalPersonName), e.additionalData?.Name);
            storage.setValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.LegalNumber), e.additionalData?.LegalNumber);
        }

        if (e.bindingContext.getNavigationPath(true) === createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.PersonType)) {
            const submitterCode = storage.getValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.TaxStatementSubmitter))?.Code;
            if (submitterCode === TaxStatementSubmitterCode.TaxAdvisor) {
                storage.setValueByPath(signatoryPath, e.value === TypeOfBusinessCode.NaturalPerson ? SignatoryPersonCode._4bIndividual_TaxAdvisorOrAttorney : SignatoryPersonCode._4cLegalEntityProvidingTaxConsultancy);
            }
        }

        if (path === hasTaxStatementSignatoryPersonPath) {
            if (e.value) {
                storage.setValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.TaxStatementSubmitter), TaxStatementSubmitterCode.Accountant);
                storage.setValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.PersonType), TypeOfBusinessCode.NaturalPerson);
                storage.setValueByPath(createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.SignatoryPerson), SignatoryPersonCode._4aGeneralAttorneyIndividualAndLegalPerson);
            } else {
                storage.setValueByPath(CompanyEntity.TaxStatementSignatoryPerson, null);
            }
            storage.refresh();
        }
    };

    setDefaultStatutoryInsuranceCompanyRate = (rate: number): void => {
        const storage = this.props.storage;
        if (!storage.data.origEntity?.DefaultStatutoryInsuranceCompanyRate && storage.getValueByPath(hasPayrollEvidencePath)) {
            storage.setValueByPath(CompanyEntity.DefaultStatutoryInsuranceCompanyRate, rate);
        }
    };

    handleNaceChange = (e: ISmartFieldChange): void => {
        const path = e.bindingContext.getPath();

        if (path === CompanyEntity.CzNace && e.triggerAdditionalTasks) {
            this.setDefaultStatutoryInsuranceCompanyRate(e.additionalData?.Rate);
        }
    };

    handleChange = async (e: ISmartFieldChange): Promise<void> => {
        this.handlePostalCodeChange(e);

        this.props.storage.handleChange(e);

        handleCurrentTemporalPropChange(e, this.props.storage as FormStorage<any>);

        this.handleTaxStatementSignatoryPersonChange(e);
        this.handlePartnerChange(e);
        this.handleIsSecondaryAddressChange(e);
        this.handleCurrentVatStatusChange(e);
        this.handleNaceChange(e);
        this.props.storage.refreshFields(e.triggerAdditionalTasks);
    };

    handleBlur = async (e: ISmartFieldBlur): Promise<void> => {
        const { storage } = this.props;

        if (e.bindingContext.getEntityPath(true) === this.postalCodePath &&
            storage.getValue(e.bindingContext)?.length !== POSTAL_CODE_LENGTH) {
            storage.setValueByPath(CompanyEntity.FinancialAdministration, null);
        }

        await storage.handleBlur(e);
        storage.refreshFields();
    };

    handlePartnerChange = async (e: ISmartFieldChange): Promise<void> => {
        const { storage } = this.props;

        const path = e.bindingContext.getPath(true);

        if (!([CompanyEntity.LegalName, CompanyEntity.LegalNumber] as string[]).includes(e.bindingContext.getEntityPath()) || !e.triggerAdditionalTasks) {
            return;
        }

        for (const partnerField of Object.values(partnerAresFields)) {
            if (e.additionalData.isAllowCreateItem && (path === CompanyEntity.LegalNumber || partnerField.to === CompanyEntity.LegalName)) {
                // currently changed field -> it was handled by storage->handleChange automatically,
                // do not need to process additional data
                // furthermore, it doesn't work well with isAllowCreateItem as it clears the data
                // (additionalData are empty, format is different - according to fieldName)
                continue;
            }

            const fieldBc = storage.data.bindingContext.navigate(partnerField.to);
            let value = getNestedValue(partnerField.from, e.additionalData) ?? (e.additionalData?.isAllowCreateItem ? null : undefined);

            if (typeof value === "string") {
                value = value.trim();
            }

            if (partnerField.to === CompanyEntity.CzNace) {
                const info = storage.getInfo(fieldBc);
                const items = info.fieldSettings.items ?? await fetchItemsByInfo(storage, info);
                const foundItem = items.find(item => item.additionalData?.MpsvCode === value);
                if (foundItem) {
                    // if CZNace code not present, then we should not set the value as it's invalid
                    storage.clearAndSetValue(fieldBc, foundItem.additionalData, true);
                    this.setDefaultStatutoryInsuranceCompanyRate(foundItem.additionalData?.Rate);
                }
            } else if (value !== undefined) {
                storage.clearAndSetValue(fieldBc, value, true);
            }
        }

        if (storage.data.bindingContext.isNew() && e.additionalData.VatStatus && !e.additionalData.isAllowCreateItem) {
            const vatStatusCode = e.additionalData.VatStatus.Code as VatStatusCode;

            this.props.storage.setValueByPath(createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.VatStatus), vatStatusCode);
            handleCurrentTemporalPropChange({
                bindingContext: storage.data.bindingContext.navigate(createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.VatStatus)),
                value: vatStatusCode,
                additionalData: e.additionalData.VatStatus
            }, this.props.storage as FormStorage<any>);
        }
        this.prefillOtherInstitutionsIdentificationNumber();
        storage.refresh(true);
    };

    prefillOtherInstitutionsIdentificationNumber = (): void => {
        const { storage } = this.props;

        const currentValue = storage.getValueByPath(CompanyEntity.OtherInstitutionsIdentificationNumber);
        if (currentValue) {
            return;
        }

        const legalNumber = storage.getValueByPath(CompanyEntity.LegalNumber);
        if (legalNumber) {
            storage.setValueByPath(CompanyEntity.OtherInstitutionsIdentificationNumber, legalNumber);
        }
    };

    handleIsSecondaryAddressChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getFullPath() === companyIsSecondaryAddressPath) {
            if (!e.value) {
                this._communicationAddressBackup = this.entity.CommunicationAddress;
                this.entity.CommunicationAddress = {};
            } else {
                this.entity.CommunicationAddress = this._communicationAddressBackup;
            }
        }
    };

    handleCurrentVatStatusChange = (e: ISmartFieldChange): void => {
        this.props.storage.refresh(true);
    };

    handleSelectAvatarConfirm = async (): Promise<void> => {
        const { storage } = this.props;
        const bindingContext = storage.data.bindingContext.navigate(createPath(CompanyEntity.Logo, "Id"));
        await storage.confirmFields([bindingContext]);
        storage.setCustomData({ isAvatarSelectionOpen: false });
        storage.refresh();
    };

    handleAvatarSelectionClose = (): void => {
        const { storage } = this.props;
        const bindingContext = storage.data.bindingContext.navigate(createPath(CompanyEntity.Logo, "Id"));
        storage.cancelFields([bindingContext]);
        storage.setCustomData({ isAvatarSelectionOpen: false });
        storage.refresh();
    };

    handleSmartTemporalPropertyDialogChange(args: ISmartFieldChange): void {
        super.handleSmartTemporalPropertyDialogChange(args);

        if (args.bindingContext.getPath() === CompanyVatStatusTemporalEntity.VatStatus) {
            if (args.value === VatStatusCode.VATRegistered) {
                const vatStatementFrequencyBc = args.bindingContext.getParent().navigate(CompanyVatStatusTemporalEntity.VatStatementFrequency);
                const isOssModeBc = args.bindingContext.getParent().navigate(CompanyVatStatusTemporalEntity.IsOssMode);

                this.props.storage.setValue(vatStatementFrequencyBc, VatStatementFrequencyCode.Monthly);
                this.props.storage.setValue(isOssModeBc, false);
            }
        }
    }

    handleFormCancel = (): void => {
        this.props.storage.reload();
    };

    renderCustomDialogs(): React.ReactElement {
        const { storage } = this.props;
        const { isAvatarSelectionOpen } = storage?.getCustomData() ?? {};
        return <>
            {isAvatarSelectionOpen && (
                <Dialog title={storage.t("Companies:Form.AvatarSelectionTitle")}
                        onClose={this.handleAvatarSelectionClose}
                        onConfirm={this.handleSelectAvatarConfirm}
                    // make smaller if we solve issue with using SimpleBar inside Dialog
                    // otherwise this has to be bigger to make space for the browser scrollbar
                        width="676px" // if this should change in the future see CompanyAvatarSelector render method columnsCount={6} in FocusManager
                        height="100%"
                        footer={<ConfirmationButtons confirmText={storage.t("Common:General.Save")}
                                                     onConfirm={this.handleSelectAvatarConfirm}
                                                     onCancel={this.handleAvatarSelectionClose}
                                                     useWrapper={false}/>}
                >
                    <CompanyAvatarSelector storage={storage}/>
                </Dialog>
            )}
        </>;
    }
}

export default withPermissionContext(CompanyFormView);
